import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

// Cloudflare Turnstile site key — replace with yours from dash.cloudflare.com > Turnstile
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

export default function LoginModal({ onClose, inline = false }) {
  const [tab, setTab]         = useState('login')
  const [email, setEmail]     = useState('')
  const [password, setPass]   = useState('')
  const [username, setUser]   = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [success, setSuccess] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileRef = useRef(null)
  const widgetIdRef  = useRef(null)

  // Load Turnstile script and render widget when on register tab
  useEffect(() => {
    if (tab !== 'register') return

    const loadTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'light',
          size: 'flexible',
        })
      }
    }

    if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script')
      script.id  = 'cf-turnstile-script'
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      script.onload = loadTurnstile
      document.head.appendChild(script)
    } else if (window.turnstile) {
      loadTurnstile()
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current) } catch (_) {}
        widgetIdRef.current = null
      }
    }
  }, [tab])

  async function handleLogin(e) {
    e.preventDefault(); setError(''); setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (error) setError(error.message)
    else onClose()
  }

  async function handleRegister(e) {
    e.preventDefault(); setError(''); setLoading(true)

    // Verify Turnstile token
    if (!turnstileToken) {
      setError('Please complete the human verification.'); setLoading(false); return
    }

    if (!username.trim()) { setError('Username is required'); setLoading(false); return }
    const { data: existing } = await supabase
      .from('profiles').select('id').eq('username', username.trim()).single()
    if (existing) { setError('Username already taken'); setLoading(false); return }

    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) { setError(error.message); setLoading(false); return }
    if (data.user) {
      await supabase.from('profiles').insert({
        id: data.user.id, username: username.trim(), email, role: 0,
      })
    }
    setLoading(false)
    setSuccess('Account created! Check your email to confirm, then log in.')
    setTab('login')
  }

  async function handleForgotPassword(e) {
    e.preventDefault(); setError(''); setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    setLoading(false)
    if (error) setError(error.message)
    else setSuccess('Check your email — reset link sent. Expires in 1 hour.')
  }

  const boxStyle = inline
    ? { background:'#fff', border:'1.5px solid var(--gray-200)', borderRadius:16, padding:28, maxWidth:400, width:'100%', margin:'0 auto' }
    : { maxWidth: 400 }

  const content = (
    <div className={inline ? '' : 'modal-box'} style={boxStyle}>
      {!inline && <button className="modal-close" onClick={onClose}>×</button>}

      {tab !== 'forgot' && (
        <div style={{ display:'flex', gap:0, marginBottom:24, background:'var(--gray-100)', borderRadius:8, padding:3 }}>
          {['login','register'].map(t => (
            <button key={t} onClick={() => { setTab(t); setError(''); setSuccess('') }}
              style={{ flex:1, padding:'8px', borderRadius:6, border:'none', cursor:'pointer', fontWeight:600, fontSize:13, transition:'all .15s', background:tab===t?'#fff':'transparent', color:tab===t?'var(--gray-900)':'var(--gray-500)', boxShadow:tab===t?'0 1px 4px rgba(0,0,0,.1)':'none' }}>
              {t === 'login' ? 'Login' : 'Register'}
            </button>
          ))}
        </div>
      )}

      {tab === 'forgot' && (
        <div style={{ marginBottom:20 }}>
          <button type="button" onClick={() => { setTab('login'); setError(''); setSuccess('') }}
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:13, marginBottom:12, display:'flex', alignItems:'center', gap:4, fontFamily:'var(--font)' }}>
            ← Back to login
          </button>
          <div style={{ fontWeight:700, fontSize:18 }}>Reset your password</div>
          <div style={{ fontSize:13, color:'var(--gray-500)', marginTop:4 }}>
            Enter your email and we'll send a reset link.
          </div>
        </div>
      )}

      {error   && <div style={{ background:'#fee2e2', color:'#dc2626', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 }}>{error}</div>}
      {success && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 }}>{success}</div>}

      {tab === 'login' && !success && (
        <form onSubmit={handleLogin}>
          <div className="field"><label>Email</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" required /></div>
          <div className="field"><label>Password</label><input type="password" value={password} onChange={e=>setPass(e.target.value)} placeholder="••••••••" required /></div>
          <button type="submit" className="btn btn-primary" style={{ width:'100%', marginTop:4 }} disabled={loading}>{loading?'Signing in…':'Sign In'}</button>
          <div style={{ textAlign:'center', marginTop:12 }}>
            <button type="button" onClick={() => { setTab('forgot'); setError(''); setSuccess('') }}
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--blue)', fontFamily:'var(--font)' }}>
              Forgot your password?
            </button>
          </div>
        </form>
      )}

      {tab === 'register' && !success && (
        <form onSubmit={handleRegister}>
          <div className="field"><label>Username</label><input value={username} onChange={e=>setUser(e.target.value)} placeholder="windsurfer_jay" required /></div>
          <div className="field"><label>Email</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" required /></div>
          <div className="field"><label>Password</label><input type="password" value={password} onChange={e=>setPass(e.target.value)} placeholder="••••••••" required minLength={6} /></div>

          {/* Cloudflare Turnstile — only on register */}
          <div style={{ marginBottom:14 }}>
            <div ref={turnstileRef}></div>
            <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:4 }}>
              Human verification powered by Cloudflare Turnstile — no annoying puzzles for most users.
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width:'100%', marginTop:4 }} disabled={loading || !turnstileToken}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
          <p style={{ fontSize:11, color:'var(--gray-400)', textAlign:'center', marginTop:12 }}>
            By registering you agree to keep it respectful. Admin accounts are assigned manually.
          </p>
        </form>
      )}

      {tab === 'forgot' && !success && (
        <form onSubmit={handleForgotPassword}>
          <div className="field"><label>Email address</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" required /></div>
          <button type="submit" className="btn btn-primary" style={{ width:'100%' }} disabled={loading}>{loading?'Sending…':'Send Reset Link'}</button>
        </form>
      )}
    </div>
  )

  if (inline) return content
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      {content}
    </div>
  )
}
